import type {ReactElement} from 'react';
import {useEffect, useMemo, useRef} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import type {CarouselItemData, CarouselProps} from '@Components/carousel/carousel.types';
import {CarouselClickBehaviour} from '@Components/carousel/carousel.types';
import {addGrid, addOrReplaceGridItems, getGridItemForStoredData, toggleGridItemSelection, updateMaxSelectionNumberForGrid} from '@Components/base-grid';
import {useHorizontalScrollSteps} from '@Hooks/useHorizontalScrollSteps';
import {noop} from '@Utils/general.util';
import {getUniqueString} from '@Utils/string.util';
import {AnimatePresence, motion} from 'framer-motion';
import styles from './carousel.module.scss';
import {useAppDispatch} from '@/hooks';

const HEIGHT = 100;
const SELECTION_BADGE_OVERFLOW = 8;

const dedupeCarouselItemsById = (items: CarouselItemData[]): CarouselItemData[] => {
  const seenIds = new Set<string>();
  return items.filter((item) => {
    if (seenIds.has(item.gridItemData.id)) {
      return false;
    }
    seenIds.add(item.gridItemData.id);
    return true;
  });
};

function Carousel({items, onNext = noop, onPrev = noop, height = HEIGHT, ...props}: CarouselProps): ReactElement {
  const carouselItemContainerRef = useRef<HTMLUListElement>(null);
  const gridID = useRef(props.gridID ?? getUniqueString());
  const {canScrollPrev, canScrollNext, scrollToPrev, scrollToNext, onContainerScroll} = useHorizontalScrollSteps(carouselItemContainerRef);
  const dispatch = useAppDispatch();
  const dedupedItems = useMemo(() => dedupeCarouselItemsById(items), [items]);
  // Room for the selection badge an item draws above itself, held inside the track, which clips its
  // overflow, and taken back off the container so it costs no layout around the row.
  const paddingBlockStart = props.maxSelectionNumber !== 1 ? SELECTION_BADGE_OVERFLOW : 0;

  const getControlButton = (className: string, iconClass: string, callback: () => void): ReactElement => {
    return (
      <Icon
        className={className}
        isDisabled={false}
        icon={iconClass}
        type={IconType.SHADOW}
        onClick={callback}
        size={props.controlButtonIconSize ?? IconSize.SIZE_ICON_20}
        shape={IconShape.CIRCLE}
        isUsedAsButton
      />
    );
  };

  const getGridItemForData = (item: CarouselItemData): ReactElement | null => {
    const naturalWidth = (item.width * height) / item.height;
    const maxWidth = item.hasFixedWidth || props.maxItemAspectRatio === undefined ? naturalWidth : props.maxItemAspectRatio * height;
    const itemWidth = Math.min(naturalWidth, maxWidth);
    const isWidthClamped = itemWidth < naturalWidth;
    return getGridItemForStoredData({
      item: {...item.gridItemData, gridItemHeight: height},
      paddedThumb: false,
      isSmallItem: true,
      gridId: gridID.current,
      gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
      className: `flex-center radius-4 ${styles.carouselItem} ${isWidthClamped ? styles.containedThumbItem : ''} ${item.gridItemData.className ?? ''}`,
      showSelectionNumber: props.maxSelectionNumber !== 1,
      showHoverOverlay: props.hasItemHoverOverlay ?? true,
      isDraggable: props.isItemDraggable,
      onItemPointerDown: props.onItemPointerDown,
      onClicked: (): void => {
        if (props.clickBehaviour === CarouselClickBehaviour.SELECT) {
          dispatch(
            toggleGridItemSelection({
              gridItemId: item.gridItemData.id,
              gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
            })
          );
        } else if (props.onSingleItemClick) {
          void props.onSingleItemClick([item.gridItemData]);
        }
        if (props.onCarouselItemClicked) {
          props.onCarouselItemClicked(item.gridItemData.id);
        }
      },

      style: {width: `${itemWidth}px`, height: `${height}px`},
    });
  };

  // The container's box does not change when items do, so the hook's resize observer cannot see a new track length.
  useEffect(() => {
    onContainerScroll();
  }, [dedupedItems, onContainerScroll]);

  useEffect(() => {
    dispatch(
      updateMaxSelectionNumberForGrid({
        gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
        maxSelectionNumber: props.maxSelectionNumber ?? 0,
      })
    );
  }, [props.maxSelectionNumber]);

  useEffect(() => {
    dispatch(
      addGrid({
        id: gridID.current,
        gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
        maxSelectionNumber: props.maxSelectionNumber,
        items: dedupedItems.map((item) => {
          return item.gridItemData;
        }),
      })
    );
    // addGrid is a no-op for a grid that survived a previous mount, which would leave stale items behind — items
    // fetched this mount (e.g. a freshly generated AI image) must be in the store or selection can't resolve them.
    dispatch(
      addOrReplaceGridItems({
        gridId: gridID.current,
        gridItems: Object.fromEntries(dedupedItems.map((item) => [item.gridItemData.id, item.gridItemData])),
      })
    );
  }, []);

  return (
    <div
      className={`${styles.carouselContainer} flex-row-justify-center`}
      style={{height: `${height + paddingBlockStart}px`, marginBlockStart: `${-paddingBlockStart}px`}}
    >
      <div className={`flex ${styles.carouselItemContainerWrapper} _full-width`}>
        <ul
          onScroll={onContainerScroll}
          ref={carouselItemContainerRef}
          className={`${styles.carouselItemContainer} spacing-m-0 _full-width _full-height`}
          style={{paddingBlockStart: `${paddingBlockStart}px`}}
        >
          {dedupedItems.map(getGridItemForData)}
        </ul>
      </div>

      <AnimatePresence initial={false}>
        {canScrollPrev ? (
          <motion.div
            className={`${styles.carouselControls} ${styles.pushToLeft}`}
            style={{top: `${props.controlButtonTopPositioning ?? paddingBlockStart / 2}px`, left: props.prevBtnLeftPositioning === undefined ? undefined : `${props.prevBtnLeftPositioning}px`}}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.2}}
            exit={{opacity: 0}}
          >
            {getControlButton(styles.prevBtn, 'icon-caret-left', (): void => {
              scrollToPrev();
              onPrev();
            })}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {canScrollNext ? (
          <motion.div
            className={`${styles.carouselControls} ${styles.pushToRight}`}
            style={{top: `${props.controlButtonTopPositioning ?? paddingBlockStart / 2}px`, right: props.nextBtnRightPositioning === undefined ? undefined : `${props.nextBtnRightPositioning}px`}}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.2}}
            exit={{opacity: 0}}
          >
            {getControlButton(styles.nextBtn, 'icon-caret-right', (): void => {
              scrollToNext();
              onNext();
            })}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default Carousel;
